import { Component, computed, inject, input, signal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { PageHeaderComponent } from '@components/page-header/page-header.component';
import { BulkPriceUpdateRequest, PricingBulkOperationsFormComponent } from '@components/pricing-bulk-operations-form/pricing-bulk-operations-form.component';
import { PricingEntityShortcutsComponent } from '@components/pricing-entity-shortcuts/pricing-entity-shortcuts.component';
import { dateFormattingOptions } from '@domain/formatting';
import { CreatePriceRequest, LocaleValue, MasterDataItem, PartnerType, Pricing, ProductType, SlaughterhouseItem } from '@hjc/masterdata-types';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AddPriceComponent, AddPriceData, AddPriceResult } from '@pages/pricing/components/add-price/add-price.component';
import { DialogHelpersService } from '@services/dialog/dialog-helpers.service';
import { LocalizationService } from '@services/localization.service';
import { NotificationService } from '@services/notification.service';
import { PartnersService } from '@services/partners.service';
import { PricingService } from '@services/pricing.service';
import { ProductsService } from '@services/products.service';
import { AgGridModule } from 'ag-grid-angular';
import { ColDef, RowClickedEvent, RowSelectionOptions, SelectionChangedEvent, themeAlpine } from 'ag-grid-community';
import Fuse from 'fuse.js';
import { isEmpty } from 'lodash-es';
import { debounceTime, Subscription } from 'rxjs';
import { SharedTranslationModule } from '@shared/translation.module';

@Component({
  selector: 'md-list-prices-for-slaughterhouse-page',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    AgGridModule,
    PageHeaderComponent,
    PricingBulkOperationsFormComponent,
    PricingEntityShortcutsComponent,
    TranslateModule,
    SharedTranslationModule
  ],
  templateUrl: './list-prices-for-slaughterhouse-page.component.html',
  styleUrl: './list-prices-for-slaughterhouse-page.component.scss',
  standalone: true
})
export class ListPricesForSlaughterhousePageComponent implements OnInit {
  private partnersService = inject(PartnersService);
  private localizationService = inject(LocalizationService);
  private productsService = inject(ProductsService);
  private pricingService = inject(PricingService);
  private notificationService = inject(NotificationService);
  private dialogHelpers = inject(DialogHelpersService);
  private router = inject(Router);
  private translate = inject(TranslateService);

  slaughterhouseId = input<string>('');

  pageTitle = signal<string>('');
  allPrices = signal<Pricing[]>([]);
  displayPrices = signal<Pricing[]>([]);
  selectedPrices = signal<Pricing[]>([]);
  products = signal<MasterDataItem[]>([]);
  isLoading = signal<boolean>(false);
  lang = signal<LocaleValue>(this.localizationService.currentLang);
  slaughterhouse = signal<SlaughterhouseItem | undefined>(undefined);
  theme = themeAlpine;

  searchInput = new FormControl<string>('', { nonNullable: true });
  searchTermSubscription?: Subscription;
  fuse?: Fuse<Pricing>;

  rowSelection: RowSelectionOptions = {
    mode: 'multiRow'
  };

  columnDefs = computed(() => this.getColumnDefs());

  constructor() {
    this.searchInput.valueChanges.pipe(debounceTime(300), takeUntilDestroyed()).subscribe(term => this.search(term));
  }

  ngOnInit(): void {
    const id = this.slaughterhouseId();
    if (id) {
      this.loadData(id);
    }
  }

  private getColumnDefs(): ColDef<Pricing>[] {
    return [
      {
        headerName: this.translate.instant('PRICE_DETAILS.PRODUCT_TYPE'),
        field: 'productType',
        valueFormatter: p => this.translate.instant(`PRODUCT_TYPES.${p.data?.productType.toUpperCase() ?? ''}`),
        sortable: true,
        flex: 1
      },
      { headerName: this.translate.instant('PRICE_DETAILS.NAME'), field: `name.${this.lang()}`, flex: 1, sortable: true },
      {
        headerName: this.translate.instant('PRICE_DETAILS.CURRENT_PRICE'),
        valueGetter: p => `${p.data?.currentRate?.rate} ${p.data?.unit}`,
        sortable: false,
        flex: 1
      },
      {
        headerName: this.translate.instant('PRICE_DETAILS.VALID_FROM'),
        field: 'currentRate.validFrom',
        valueFormatter: p => new Date(p.value).toLocaleDateString(this.lang()),
        sortable: true,
        flex: 1
      },
      { headerName: this.translate.instant('FIELDS.SYSTEM.CREATED_BY'), valueGetter: p => p.data?.createdBy, sortable: true, filter: true, flex: 1 },
      {
        headerName: this.translate.instant('FIELDS.SYSTEM.CREATED_AT'),
        field: 'createdAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions),
        sortable: true,
        flex: 1
      },
      { headerName: this.translate.instant('FIELDS.SYSTEM.UPDATED_BY'), valueGetter: p => p.data?.updatedBy, sortable: true, filter: true, flex: 1 },
      {
        headerName: this.translate.instant('FIELDS.SYSTEM.UPDATED_AT'),
        field: 'updatedAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions),
        sortable: true,
        flex: 1
      }
    ];
  }

  async loadData(id: string) {
    this.isLoading.set(true);

    try {
      const [slaughterhouse, prices] = await Promise.all([this.partnersService.getSlaughterhouseById(id), this.pricingService.getPricingForSlaughterhouse(id)]);
      this.slaughterhouse.set(slaughterhouse.data);
      this.pageTitle.set(
        slaughterhouse
          ? `${this.translate.instant('PRICING_LIST.PRICE_FOR')} ${slaughterhouse.data.name[this.lang()]}`
          : this.translate.instant('PRICE_DETAILS.UNKNOWN')
      );
      this.allPrices.set(prices.data || []);
      this.displayPrices.set(prices.data || []);

      this.initFuse();
    } catch (error) {
      console.error('Failed to load slaughterhouse data', error);
      this.notificationService.error(this.translate.instant('LOAD_ERROR'));
    } finally {
      this.isLoading.set(false);
    }
  }

  initFuse() {
    this.fuse = new Fuse<Pricing>(this.allPrices(), {
      keys: [`product.productGroup.name.${this.lang()}`, `product.name.${this.lang()}`, `product.productType.name.${this.lang()}`],
      threshold: 0.3
    });
  }

  search(term: string) {
    if (!this.fuse) return;

    this.displayPrices.set(!term || term.length === 0 ? this.allPrices() : this.fuse.search(term).map(r => r.item));
  }

  onSelectionChanged(event: SelectionChangedEvent<Pricing>) {
    this.selectedPrices.set(event.api.getSelectedRows());
  }

  async handleBulkPriceUpdate(request: BulkPriceUpdateRequest) {
    this.isLoading.set(true);

    try {
      const response = await this.pricingService.updatePrices(request.itemIds, request.changeFactor, request.changeType, request.validFrom);

      if (response.data?.updatedPrices?.length) {
        // Merge updated prices with existing ones
        this.mergeUpdatedPrices(response.data.updatedPrices);
        this.notificationService.success(this.translate.instant('PRICE_UPDATE_SUCCESS'));
      } else {
        // Fall back to full reload if no updated prices returned
        await this.loadData(this.slaughterhouseId());
        this.notificationService.success(this.translate.instant('PRICE_UPDATE_SUCCESS'));
      }
    } catch (error) {
      console.error('Failed to update prices:', error);
      this.notificationService.error(this.translate.instant('PRICE_UPDATE_ERROR'));
    } finally {
      this.isLoading.set(false);
    }
  }

  private mergeUpdatedPrices(updatedPrices: Pricing[]) {
    if (!updatedPrices.length) return;

    // Map of updated prices by ID for quick lookups
    const updatedPriceMap = new Map(updatedPrices.map(price => [price.id, price]));

    // Update allPrices by replacing updated items
    this.allPrices.update(prices => prices.map(price => (updatedPriceMap.has(price.id) ? updatedPriceMap.get(price.id)! : price)));

    // If we have a search term active, apply it again
    if (this.searchInput.value) {
      this.search(this.searchInput.value);
    } else {
      // Otherwise update displayed prices
      this.displayPrices.update(prices => prices.map(price => (updatedPriceMap.has(price.id) ? updatedPriceMap.get(price.id)! : price)));
    }

    this.initFuse();
  }

  async refreshPriceData(data: Pricing) {
    this.isLoading.set(true);

    try {
      // Update all prices with the new one
      this.allPrices.update(prices => [...prices, data]);

      // If there's an active search, reapply it
      if (this.searchInput.value && this.searchInput.value.length > 0) {
        this.search(this.searchInput.value);
      } else {
        this.displayPrices.set(this.allPrices());
      }

      this.initFuse();
    } catch (error) {
      console.error('Error refreshing price data:', error);
      this.notificationService.error(this.translate.instant('PRICE_REFRESH_ERROR'));
    } finally {
      this.isLoading.set(false);
    }
  }

  async showAddPriceDialog() {
    this.isLoading.set(true);

    try {
      if (isEmpty(this.products())) {
        const productsResponse = await this.productsService.getByProducts();
        const serviceProductsResponse = await this.productsService.getServices();
        // Combine by products and service products
        const allProducts = [...(productsResponse.data || []), ...(serviceProductsResponse.data || [])];
        this.products.set(allProducts || []);
      }

      const productsList = this.products();
      if (!productsList || productsList.length === 0) {
        this.notificationService.error(this.translate.instant('PRODUCTS_MISSING'));
        return;
      }

      const existingProductIds = new Set(this.allPrices().map(p => p.productId));
      const availableProducts = productsList.filter(product => !existingProductIds.has(product.id));

      if (!availableProducts.length) {
        this.notificationService.error(this.translate.instant('NOTIFICATIONS.ALL_PRODUCTS_HAVE_PRICES'));
        return;
      }

      const dialogData: AddPriceData = {
        items: [...availableProducts],
        measure: [...this.pricingService.measures]
      };

      await this.dialogHelpers.openAndHandleResult<AddPriceComponent, AddPriceData, AddPriceResult>(
        AddPriceComponent,
        { width: '600px', data: dialogData },
        async result => {
          const priceObj: CreatePriceRequest = {
            name: result.itemName,
            productId: result.itemId,
            productName: result.itemName,
            productType: result.itemType as ProductType,
            partnerId: this.slaughterhouseId(),
            partnerName: this.slaughterhouse()!.name,
            partnerType: result.itemType as PartnerType,
            unit: result.unit,
            rates: [{ rate: result.price, validFrom: result.validFrom.toISOString() }]
          };
          const response = await this.pricingService.addPrice(priceObj);

          this.notificationService.success(this.translate.instant('NOTIFICATIONS.PRICE_ADD_SUCCESS'));
          await this.refreshPriceData(response.data);
        },
        this.translate.instant('NOTIFICATIONS.PRICE_ADD_ERROR')
      );
    } catch (error) {
      console.error('Failed to prepare dialog:', error);
      this.notificationService.error(this.translate.instant('NOTIFICATIONS.FORM_OPEN_ERROR'));
    } finally {
      this.isLoading.set(false);
    }
  }

  onRowClicked(event: RowClickedEvent<Pricing, unknown>) {
    if (event.data?.id) {
      // Navigate to the price details while preserving the current context in the URL
      this.router.navigate(['/app/pricing/partners/slaughterhouses', this.slaughterhouseId(), 'price', event.data.id]);
    }
  }
}
